import sentry, {
  addBreadcrumb as addSentryBreadcrumb,
  captureException as captureSentryException,
  setUser as setSentryUser
} from "lib/sentry";
import type { MonitoringBreadcrumb, MonitoringUser } from "lib/sentry";

interface SpanCollector {
  action?: string;
  tags: Record<string, unknown>;
  params: Record<string, unknown>;
  setAction: (action: string) => SpanCollector;
  setTags: (tags: Record<string, unknown>) => SpanCollector;
  setParams: (params: Record<string, unknown>) => SpanCollector;
}

type SpanCallback = (span: SpanCollector) => void;

const createSpanCollector = (): SpanCollector => {
  const span: SpanCollector = {
    tags: {},
    params: {},
    setAction(action: string) {
      span.action = action;
      return span;
    },
    setTags(tags: Record<string, unknown>) {
      span.tags = { ...span.tags, ...tags };
      return span;
    },
    setParams(params: Record<string, unknown>) {
      span.params = { ...span.params, ...params };
      return span;
    }
  };

  return span;
};

const normalizeError = (error: unknown): Error => {
  if (error instanceof Error) return error;
  return new Error(String(error));
};

const collectSpanContext = (callback?: SpanCallback): SpanCollector => {
  const span = createSpanCollector();
  if (!callback) return span;

  try {
    callback(span);
  } catch (error) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("Error monitoring: Failed to collect span context", error);
    }
  }

  return span;
};

export const sendError = (error: unknown, callback?: SpanCallback): void => {
  const normalizedError = normalizeError(error);
  const spanContext = collectSpanContext(callback);

  captureSentryException(normalizedError, {
    action: spanContext.action,
    tags: spanContext.tags,
    params: spanContext.params
  });
};

export const addBreadcrumb = (breadcrumb: MonitoringBreadcrumb): void => {
  addSentryBreadcrumb(breadcrumb);
};

export const setUser = (user: MonitoringUser | null): void => {
  setSentryUser(user);
};

const errorMonitoring = {
  sendError,
  addBreadcrumb,
  setUser,
  sentry
};

export default errorMonitoring;
